import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';

import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { ProjectIndustriesListComponent } from '@app/shared/components/project-industries-list/project-industries-list.component';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { CompanyIndustriesDataService } from '@app/shared/services/company-industries-data.service';
import { ProjectIndustriesDataService } from '@app/shared/services/project-industries-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { Company } from '@app/shared/types/models/company/company.model';
import { Industry } from '@app/shared/types/models/industry/industry.model';
import {
  ProjectIndustry,
  SelectIndustryDialogData
} from '@app/shared/types/models/project-industry/project-industry.model';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-view',
  templateUrl: './project-industries-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    UiInputComponent,
    UiPageWrapperComponent,
    TranslatePipe,
    ProjectIndustriesListComponent,
    UiButtonComponent,
    UiSpinnerDirective
  ]
})
export class ProjectIndustriesViewComponent {
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly projectIndustriesService: ProjectIndustriesDataService = inject(ProjectIndustriesDataService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly companyIndustriesDataService: CompanyIndustriesDataService = inject(CompanyIndustriesDataService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly projectIndustriesDataService: ProjectIndustriesDataService = inject(ProjectIndustriesDataService);

  protected readonly projectIndustries: Signal<ProjectIndustry[]> = computed(() =>
    this.projectIndustriesService.getProjectIndustries(this.activeProjectId())()
  );
  protected readonly industries = computed(() =>
    this.companyIndustriesDataService.getCompanyIndustries(this.activeCompany()?.id)()
  );
  protected searchValue: WritableSignal<string> = signal(undefined);
  protected activeProjectId: Signal<string> = this.activeProjectService.activeProjectId;
  protected activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;
  protected filteredItems: Signal<ProjectIndustry[]> = this.projectIndustries;

  public constructor() {
    if (this.activeProjectId()) {
      this.projectIndustriesService.loadProjectIndustries(this.activeProjectId());
    }
    this.filteredItems = computed(() => {
      const trimmedSearch = this.searchValue()?.trim()?.toLowerCase();
      if (trimmedSearch) {
        return this.projectIndustries()?.filter(projectIndustry =>
          projectIndustry.industry().name.toLowerCase().includes(trimmedSearch)
        );
      } else {
        return this.projectIndustries();
      }
    });
  }

  public openSelectProjectIndustryDialog(): void {
    const modalData: SelectIndustryDialogData = {
      industries: this.industries,
      title: 'project-industries.modals.select-company-industry-modal-title'
    };
    this.dialogService
      .openSelectIndustryDialog(modalData)
      .afterClosed()
      .subscribe((result: Industry) => {
        if (result) this.selectIndustryAdmin(result);
      });
  }

  public selectIndustryAdmin(selectedIndustry: Industry): void {
    this.dialogService
      .opensSelectIndustryAdminDialog(null)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.projectIndustriesDataService.addIndustryToProject(
            this.activeProjectId(),
            selectedIndustry.id,
            result.selectedAdmin
          );
        }
      });
  }
}
